import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import api from "../../api";
import { LEVEL_REQUIREMENTS } from "../../constants/items";
import "./ManageItem.css";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

const ManageItems = () => {
  document.title = "Pokemon Radiance  | Manage Items";

  // Tab state: "all", "equip", "chests"
  const [activeTab, setActiveTab] = useState("all");
  // Inventory from /v1/inventory endpoint
  const [inventory, setInventory] = useState([]);
  // Full item details from /v1/store endpoint
  const [storeItems, setStoreItems] = useState([]);
  // Loading state for common data
  const [loading, setLoading] = useState(true);
  // Search query for filtering items in All Items
  const [searchQuery, setSearchQuery] = useState("");
  // Mining level for equipment requirement checks
  const [miningLevel, setMiningLevel] = useState(0);

  // Equipment data from /v1/equipment endpoint
  const [equipData, setEquipData] = useState(null);
  const [equipLoading, setEquipLoading] = useState(true);

  // Popup state variables
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupColor, setPopupColor] = useState("");

  // New state for chest opening animation (only for color boxes)
  const [chestOpening, setChestOpening] = useState(false);
  // Using array destructuring with empty variable to avoid lint warnings
  const [, setChestCandidates] = useState([]);
  const [, setFinalChestPokemon] = useState(null);
  const [chestDisplayImage, setChestDisplayImage] = useState(null);
  const [chestRewardItems, setChestRewardItems] = useState(null);

  // Fetch inventory and store items on mount
  useEffect(() => {
    const fetchCommonData = async () => {
      try {
        const [inventoryRes, storeRes, miningRes] = await Promise.all([
          api.get("/v1/inventory", { withCredentials: true }),
          api.get("/v1/store", { withCredentials: true }),
          api.get("/v1/mining/info", { withCredentials: true }),
        ]);
        // Filter out any items with amount 0.
        setInventory(
          inventoryRes.data.filter(
            (item) => item.amount > 0 || item.stack_amount > 0,
          ),
        );
        setStoreItems(storeRes.data);
        // Set mining level for equipment requirement checks
        setMiningLevel(miningRes.data.mining_level || 0);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching common data:", error);
        setLoading(false);
      }
    };
    fetchCommonData();
  }, []);

  // When active tab is Equip Items, fetch equipment data
  useEffect(() => {
    if (activeTab === "equip") {
      const fetchEquipData = async () => {
        try {
          const res = await api.get("/v1/equipment", { withCredentials: true });
          setEquipData(res.data);
          setEquipLoading(false);
        } catch (error) {
          console.error("Error fetching equipment data:", error);
          setEquipLoading(false);
        }
      };
      fetchEquipData();
    }
  }, [activeTab]);

  // Define safe and chest (color box) item IDs.
  const safeItemIds = [58, 222258, 333358, 64];
  const chestBoxIds = [180, 181, 182, 183, 184, 185, 186];

  const equipableInStock = (equipData?.equipable || []).filter(
    (item) => item.amount > 0
  );

  // Filter the inventory to include only safe and chest items with amount > 0.
  const chestInventory = inventory.filter(
    (invItem) =>
      (safeItemIds.includes(invItem.item) ||
        chestBoxIds.includes(invItem.item)) &&
      invItem.amount > 0,
  );

  // Create a mapping from item id to its full details (name, image, etc.)
  const storeItemsMap = storeItems.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
  // Also create mapping from item name to item details (for equipped items lookup)
  const storeItemsByName = storeItems.reduce((acc, item) => {
    acc[item.name] = item;
    return acc;
  }, {});

  // Create mapping for inventory by item id (only items with amount > 0).
  const inventoryMap = inventory.reduce((acc, inv) => {
    if (inv.amount > 0 || inv.stack_amount > 0) {
      const key = inv.item ?? inv.id;
      acc[key] = inv;
    }
    return acc;
  }, {});
  // Filter inventory for All Items tab based on search query and amount > 0.
  const filteredInventory = inventory.filter((invItem) => {
    const item = storeItemsMap[invItem.item];
    if (!item) return false;
    return (
      invItem.amount > 0 &&
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Handler to unequip an item by item id.
  const handleUnequip = async (itemId) => {
    try {
      const charmIds = new Set([193, 194, 121]);
      const equipableCharm = charmIds.has(itemId)
        ? (equipData?.equipable || []).find((item) => item.id === itemId)
        : null;

      if (equipableCharm && equipableCharm.stack_amount) {
        for (let i = 0; i < equipableCharm.stack_amount; i += 1) {
          await api.post(
            "/v1/unequip",
            { item: itemId },
            { withCredentials: true },
          );
        }
      } else {
        await api.post(
          "/v1/unequip",
          { item: itemId },
          { withCredentials: true },
        );
      }
      // Refresh equipment data.
      const res = await api.get("/v1/equipment", { withCredentials: true });
      setEquipData(res.data);
      // Refresh inventory data.
      const invRes = await api.get("/v1/inventory", { withCredentials: true });
      setInventory(
        invRes.data.filter((item) => item.amount > 0 || item.stack_amount > 0),
      );
    } catch (error) {
      console.error("Error unequipping item:", error);
      let message = "Error unequipping item.";
      if (
        error.response.data &&
        error.response.data &&
        error.response.data.detail
      ) {
        message = error.response.detail;
      }
      setPopupMessage(message);
      setPopupColor("red");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  };

  // Handler to equip an item.
  const handleEquip = async (itemId) => {
    // Check if the item has a mining level requirement
    if (LEVEL_REQUIREMENTS[itemId] && miningLevel < LEVEL_REQUIREMENTS[itemId]) {
      const requiredLevel = LEVEL_REQUIREMENTS[itemId];
      setPopupMessage(`You need mining level ${requiredLevel} to equip this item.`);
      setPopupColor("red");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
      return;
    }

    try {
      const charmIds = new Set([193, 194, 121]);
      const equipableCharm = charmIds.has(itemId)
        ? (equipData?.equipable || []).find((item) => item.id === itemId)
        : null;

      if (equipableCharm) {
        const maxStack = equipableCharm.max_stack_amount > 0
          ? equipableCharm.max_stack_amount
          : equipableCharm.amount;
        const remaining = Math.max(
          0,
          maxStack - (equipableCharm.stack_amount || 0),
        );

        for (let i = 0; i < remaining; i += 1) {
          await api.post("/v1/equip", { item: itemId }, { withCredentials: true });
        }
      } else {
        await api.post("/v1/equip", { item: itemId }, { withCredentials: true });
      }

      // Refresh equipment data.
      const res = await api.get("/v1/equipment", { withCredentials: true });
      setEquipData(res.data);
      // Refresh inventory data.
      const invRes = await api.get("/v1/inventory", { withCredentials: true });
      setInventory(
        invRes.data.filter((item) => item.amount > 0 || item.stack_amount > 0),
      );
    } catch (error) {
      console.error("Error equipping item:", error);
      let message = "Error equipping item.";
      setChestDisplayImage(null);
      setPopupMessage(message);
      setPopupColor("red");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  };

  // Handler for safe items with modern modal display.
  const handleUseItem = async (itemId) => {
    try {
      // Get the item details for display
      const safeItemDetails = storeItemsMap[itemId];
      setChestRewardItems(null);
      
      // Show opening animation
      setChestOpening(true);
      setChestDisplayImage(`/${safeItemDetails?.image || 'items/safe.png'}`);
      setPopupMessage(`Opening ${safeItemDetails?.name || 'Safe'}...`);
      setPopupColor("#1d222e");
      
      // Call API to open the safe
      const res = await api.post(
        "/v1/use_item",
        { item_id: itemId },
        { withCredentials: true },
      );
      
      // Update the display with results
      if (res.data?.gems_awarded && Array.isArray(res.data.gems_awarded)) {
        setChestRewardItems(res.data.gems_awarded);
        setPopupMessage(`You opened a ${safeItemDetails?.name || 'Bag of Gems'}!`);
      } else {
        setPopupMessage(res.data.message);
      }
      setChestDisplayImage(`/${safeItemDetails?.image || 'items/safe.png'}`);
      
      // Refresh inventory after using the item but keep modal open
      // The user will close the modal manually with the close button
      refreshInventory();
    } catch (error) {
      console.error("Error using item:", error);
      let message = "Error opening safe.";
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      
      // Show error in modal
      setChestOpening(true);
      setPopupMessage(message);
      // Error color is now handled by the fixed white text color
      // Keep modal open for user to close manually
    }
  };
  
  // Helper function to refresh inventory
  const refreshInventory = async () => {
    try {
      const invRes = await api.get("/v1/inventory", { withCredentials: true });
      setInventory(
        invRes.data.filter((item) => item.amount > 0 || item.stack_amount > 0),
      );
    } catch (error) {
      console.error("Error refreshing inventory:", error);
    }
  };

  // Handler for chest boxes (color boxes) with flipping animation.
  const handleUseChest = async (itemId) => {
    try {
      // Reset chest-related state for a clean start.
      setFinalChestPokemon(null);
      setChestCandidates([]);
      setChestDisplayImage(null);
      setPopupMessage(null);
      setPopupColor(null);
      setChestRewardItems(null);

      // First, call the use_item endpoint (POST) for the chest box.
      const useRes = await api.post(
        "/v1/use_item",
        { item_id: itemId },
        { withCredentials: true },
      );
      const chestData = useRes.data;
      // Expected response format:
      // { "type": "luminous", "pokemon_name": "LuminousPichu", "gender": "F", "level": 5 }
      setFinalChestPokemon(chestData);

      // Then, call the GET endpoint for that box type from account "l33t".
      const typeRes = await api.get(`/v1/pokemon/type/economy/${chestData.type}`, {
        withCredentials: true,
      });
      const candidates = typeRes.data; // an array of candidate Pokémon.
      setChestCandidates(candidates);

      // Begin the image flipping animation (only for chest boxes).
      setChestOpening(true);
      const intervalId = setInterval(() => {
        if (candidates.length > 0) {
          const randomCandidate =
            candidates[Math.floor(Math.random() * candidates.length)];
          // Construct candidate image URL.
          const randomImageUrl = `/sprites/${randomCandidate.name}.png`;
          setPopupMessage("Randomly selecting a Pokemon.....");
          setPopupColor("gray");
          setChestDisplayImage(randomImageUrl);
        }
      }, 300);

      const categoryColor = (pokemonName) => {
        if (pokemonName.toLowerCase().includes("ruby")) return "#FF0000";
         if (pokemonName.toLowerCase().includes("sapphire")) return "#0F52BA";
        if (pokemonName.toLowerCase().includes("golden")) return "#d9c200";
        if (pokemonName.toLowerCase().includes("diamond")) return "#85c7ff";
        if (pokemonName.toLowerCase().includes("emerald")) return "#15ff00";
        if (pokemonName.toLowerCase().includes("shadow")) return "#2a0c32";
        return "#00ff00"; // Default green for regular Pokémon
      };

      // After 6 seconds, stop the flipping and show the final image.
      setTimeout(() => {
        clearInterval(intervalId);
        const finalImageUrl = `/sprites/${chestData.pokemon_name}.png`;
        setChestDisplayImage(finalImageUrl);
        
        // Hide gender if it's 'U' (unknown)
        const genderDisplay = chestData.gender === 'U' ? '' : `${chestData.gender} `;
        
        setPopupMessage(
          `You opened a ${chestData.type} box and received ${chestData.pokemon_name} ${genderDisplay}Lvl ${chestData.level.toLocaleString()}!`,
        );
        setPopupColor(categoryColor(chestData.pokemon_name));
        // Keep the chest opening modal open instead of switching to popup
        // setChestOpening(false);
        // setShowPopup(true);
        // setTimeout(() => setShowPopup(false), 5000);
        // Refresh inventory.
        api
          .get("/v1/inventory", { withCredentials: true })
          .then((invRes) =>
            setInventory(
              invRes.data.filter(
                (item) => item.amount > 0 || item.stack_amount > 0,
              ),
            ),
          );
      }, 6000);
    } catch (error) {
      console.error("Error using chest item:", error);
      let message = "Error using chest item.";
      if (
        error.response.data &&
        error.response.data &&
        error.response.data.detail
      ) {
        message = error.response.detail;
      }
      setChestDisplayImage(null);
      setPopupMessage(message);
      setPopupColor("red");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  };

  const formatNumber = (num) => {
    return Number(num).toLocaleString();
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Pokémon Radiance" breadcrumbItem="Manage Items" />
          <Row className="mb-4">
            <Col>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "all" })}
                    onClick={() => setActiveTab("all")}
                    style={{ cursor: "pointer" }}
                  >
                    All Items
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "equip" })}
                    onClick={() => setActiveTab("equip")}
                    style={{ cursor: "pointer" }}
                  >
                    Equip Items
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "chests" })}
                    onClick={() => setActiveTab("chests")}
                    style={{ cursor: "pointer" }}
                  >
                    Chests
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent activeTab={activeTab} className="p-3">
                {/* All Items Tab */}
                <TabPane tabId="all">
                  {loading ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <span>Loading items...</span>
                    </div>
                  ) : (
                    <div className="equipment-container">
                      <div className="equipment-header">
                        <h1 className="equipment-title">All Items</h1>
                        <p className="equipment-subtitle">View your  item collection</p>
                      </div>

                      <div className="inventory-section">
                        <div className="inventory-header">
             
                          <div className="search-container">
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Search items..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <div className="search-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="21 21l-4.35-4.35"></path>
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div className="inventory-grid">
                          {filteredInventory.map((invItem) => {
                            const item = storeItemsMap[invItem.item];
                            if (!item) return null;
                            return (
                              <div key={invItem.item} className="inventory-item">
                                <img
                                  src={`/${item.image}`}
                                  alt={item.name}
                                  className="inventory-item-image"
                                />
                                <div className="inventory-item-name">{item.name}</div>
                                <div className="inventory-item-quantity">
                                  {formatNumber(invItem.amount)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {filteredInventory.length === 0 && (
                          <div style={{textAlign: 'center', color: '#b8c5d6', padding: '40px'}}>
                            <p>No items found</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabPane>
                {/* Professional Equipment Tab */}
                <TabPane tabId="equip">
                  {equipLoading || !equipData ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <span>Loading equipment...</span>
                    </div>
                  ) : (
                    <div className="equipment-container">
                      <div className="equipment-header">
                        <h1 className="equipment-title">Equipment Manager</h1>
                        <p className="equipment-subtitle">Equip items to enhance your character's abilities</p>
                      </div>

                      {/* Character Equipment Display */}
                      <div className="character-equipment-section" style={{ display: 'flex', gap: '20px' }}>
                        {/* Avatar moved to the left side */}
                        <div className="character-display">
                          <div className="character-avatar">
                            <img
                              src={`/avatars/${localStorage.getItem('avatar') || '1'}.png`}
                              alt="Player Avatar"
                              className="player-avatar-image"
                              onError={(e) => {
                                e.target.src = '/avatars/default.png';
                              }}
                            />
                          </div>
                        </div>

                        <div className="equipment-slots">
                          {["head", "neck", "body", "handhelds", "feet"].map((slot) => {
                            const equippedName = equipData.equipped[slot];
                            const itemDetail = equippedName ? storeItemsByName[equippedName] : null;
                            const invRecord = itemDetail ? inventoryMap[itemDetail.id] : null;
                            
                            return (
                              <div 
                                key={slot} 
                                className={`equipment-slot slot-${slot} ${equippedName ? 'occupied' : ''}`}
                                title={equippedName || `Empty ${slot} slot`}
                              >
                                {equippedName && itemDetail ? (
                                  <>
                                    <img
                                      src={`/${itemDetail.image}`}
                                      alt={equippedName}
                                      className="equipment-item-image"
                                    />
                                    <div className="equipment-actions">
                                      <button
                                        className="action-button unequip-button"
                                        onClick={() => handleUnequip(itemDetail.id)}
                                        title="Unequip item"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                    <div className="equipment-tooltip">
                                      {equippedName}
                                      {invRecord && ` (${invRecord.stack_amount})`}
                                    </div>
                                  </>
                                ) : (
                                  <>
                             
                                    <div className="equipment-tooltip">
                                      Empty {slot} slot
                                    </div>
                                  </>
                                )}
                                <div className="equipment-slot-label">{slot}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Ring slots section - positioned underneath the feet */}
                      <div className="ring-slots-section">
                        <div className="ring-slots-label desktop-only">Ring Slots</div>
                        <div className="ring-slots">
                          {[1, 2, 3].map((ringIndex) => {
                            // For ring slots, we need special handling
                            // Only slot 1 can have the equipment ring
                            // Show the equipped ring for each slot
                            const ringSlot = `ring${ringIndex}`;
                            const ringEquipped = equipData.equipped[ringSlot];
                            const isRingSlotOccupied = ringEquipped !== null;
                            const equippedName = isRingSlotOccupied ? ringEquipped : null;
                            const itemDetail = equippedName ? storeItemsByName[equippedName] : null;
                            const invRecord = itemDetail ? inventoryMap[itemDetail.id] : null;
                            
                            // Add a special class for ring slots 2 and 3 to indicate they're available
                            const ringSlotClass = ringIndex > 1 ? 'ring-slot-available' : '';

                            return (
                              <div 
                                key={ringSlot} 
                                className={`equipment-slot slot-ring ${isRingSlotOccupied ? 'occupied' : ''} ${ringSlotClass}`}
                                title={equippedName || `Empty ring slot ${ringIndex}`}
                                style={{
                                  border: ringIndex > 1 ? '2px dashed rgba(255, 215, 0, 0.5)' : undefined,
                                  background: ringIndex > 1 ? 'rgba(0, 0, 0, 0.2)' : undefined
                                }}
                              >
                                {isRingSlotOccupied && itemDetail ? (
                                  <>
                                    <img
                                      src={`/${itemDetail.image}`}
                                      alt={equippedName}
                                      className="equipment-item-image"
                                    />
                                    <div className="equipment-actions">
                                      <button
                                        className="action-button unequip-button"
                                        onClick={() => handleUnequip(itemDetail.id)}
                                        title="Unequip item"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                    <div className="equipment-tooltip">
                                      {equippedName}
                                      {invRecord && ` (${invRecord.stack_amount})`}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="equipment-tooltip">
                                      Empty ring slot {ringIndex}
                                    </div>
                                    <div className="ring-number mobile-only">{ringIndex}</div>
                                  </>
                                )}
                                <div className="equipment-slot-label desktop-only">Ring {ringIndex}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {/* Inventory Section */}
                      <div className="inventory-section">
                        <div className="inventory-header">   
                          <div className="search-container">
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Search equipment..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <div className="search-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="21 21l-4.35-4.35"></path>
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div className="inventory-grid">
                          {equipableInStock
                            .filter(eqItem => {
                              const itemDetail = storeItemsMap[eqItem.id];
                              return itemDetail && itemDetail.name.toLowerCase().includes(searchQuery.toLowerCase());
                            })
                            .map((eqItem) => {
                              const itemDetail = storeItemsMap[eqItem.id];
                              if (!itemDetail) return null;
                              
                              // Use slot from backend if available, otherwise determine from item properties
                              let itemSlot = eqItem.slot;
                              if (!itemSlot) {
                                ["head", "handhelds", "feet", "neck", "body"].forEach((s) => {
                                  if (itemDetail[s] > 0) itemSlot = s;
                                });
                              }

                              // Special handling for ring items
                              let slotAvailable = true;
                              if (itemSlot === 'ring') {
                                // Check if this ring is already equipped in any slot
                                const isAlreadyEquipped = 
                                  (equipData.equipped['ring1'] === itemDetail.name) ||
                                  (equipData.equipped['ring2'] === itemDetail.name) ||
                                  (equipData.equipped['ring3'] === itemDetail.name);
                                
                                // Count how many ring slots are occupied
                                const isRing1Occupied = !!equipData.equipped['ring1'];
                                const isRing2Occupied = !!equipData.equipped['ring2'];
                                const isRing3Occupied = !!equipData.equipped['ring3'];
                                
                                // Available if already equipped or if there's at least one free slot
                                slotAvailable = isAlreadyEquipped || !(isRing1Occupied && isRing2Occupied && isRing3Occupied);
                              } else {
                                // For other equipment types, check normally
                                slotAvailable = !equipData.equipped[itemSlot] || 
                                              equipData.equipped[itemSlot] === itemDetail.name;
                              }
                              
                              // Check if we've reached the max stack amount
                              const maxStackReached = eqItem.max_stack_amount > 0 && 
                                                    eqItem.stack_amount >= eqItem.max_stack_amount;
                              
                              // Check if item has a level requirement and if player meets it
                              const hasLevelRequirement = LEVEL_REQUIREMENTS[eqItem.id] !== undefined;
                              const meetsLevelRequirement = !hasLevelRequirement || miningLevel >= LEVEL_REQUIREMENTS[eqItem.id];
                              
                              // Check if all ring slots are occupied (for ring items)
                              const allRingSlotsOccupied = itemSlot === 'ring' && 
                                !!equipData.equipped['ring1'] && 
                                !!equipData.equipped['ring2'] && 
                                !!equipData.equipped['ring3'] &&
                                equipData.equipped['ring1'] !== itemDetail.name &&
                                equipData.equipped['ring2'] !== itemDetail.name &&
                                equipData.equipped['ring3'] !== itemDetail.name;
                              
                              // Can equip only if slot is available, max stack not reached, and meets level requirement
                              const canEquip = slotAvailable && !maxStackReached && meetsLevelRequirement;

                              return (
                                <div 
                                  key={eqItem.id} 
                                  className={`inventory-item equipable ${canEquip ? 'can-equip' : ''}`}
                                >
                                  <img
                                    src={`/${itemDetail.image}`}
                                    alt={itemDetail.name}
                                    className="inventory-item-image"
                                  />
                                  <div className="inventory-item-name">{itemDetail.name}</div>
                                  <div className="inventory-item-quantity">{eqItem.amount}</div>
                                  <button
                                    className="equip-button"
                                    onClick={() => handleEquip(eqItem.id)}
                                    disabled={!canEquip}
                                    title={hasLevelRequirement && !meetsLevelRequirement ? 
                                        `Requires Mining Level ${LEVEL_REQUIREMENTS[eqItem.id]}` : 
                                        allRingSlotsOccupied ? 'All ring slots are occupied' :
                                        !slotAvailable ? 'This slot is already occupied' : 
                                        maxStackReached ? 'Maximum number already equipped' : 'Equip this item'}
                                  >
                                    {allRingSlotsOccupied ? 'All Slots Full' :
                                     !slotAvailable && itemSlot !== 'ring' ? 'Slot Occupied' : 
                                     maxStackReached ? 'Max Equipped' : 
                                     (hasLevelRequirement && !meetsLevelRequirement) ? 
                                     `Requires Level ${LEVEL_REQUIREMENTS[eqItem.id]}` : 'Equip'}
                                  </button>
                                </div>
                              );
                            })}
                        </div>
                        
                        {equipableInStock.length === 0 && (
                          <div style={{textAlign: 'center', color: '#b8c5d6', padding: '40px'}}>
                            <p>No equipable items available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabPane>
                {/* Chests Tab */}
                <TabPane tabId="chests">
                  {loading ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <span>Loading chests...</span>
                    </div>
                  ) : (
                    <div className="equipment-container">
                      <div className="equipment-header">
                        <h1 className="equipment-title">Color Chests</h1>
                        <p className="equipment-subtitle">Open chests to discover valuable rewards</p>
                      </div>

                      <div className="inventory-section">
                        <div className="inventory-header">    
                          <div className="search-container">
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Search chests..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <div className="search-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="21 21l-4.35-4.35"></path>
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div className="inventory-grid">
                          {chestInventory
                            .filter(invItem => {
                              const item = storeItemsMap[invItem.item];
                              return item && item.name.toLowerCase().includes(searchQuery.toLowerCase());
                            })
                            .map((invItem) => {
                              const item = storeItemsMap[invItem.item];
                              if (!item) return null;

                              return (
                                <div key={invItem.item} className="inventory-item chest-item">
                                  <img
                                    src={`/${item.image}`}
                                    alt={item.name}
                                    className="inventory-item-image"
                                  />
                                  <div className="inventory-item-name">{item.name}</div>
                                  <div className="inventory-item-quantity">
                                    {formatNumber(invItem.amount)}
                                  </div>
                                  <button
                                    className="open-chest-button"
                                    onClick={() =>
                                      safeItemIds.includes(invItem.item)
                                        ? handleUseItem(invItem.item)
                                        : handleUseChest(invItem.item)
                                    }
                                  >
                                    Open Chest
                                  </button>
                                </div>
                              );
                            })}
                        </div>
                        
                        {chestInventory.length === 0 && (
                          <div style={{textAlign: 'center', color: '#b8c5d6', padding: '40px'}}>
                            <p>No chests available to open</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabPane>
              </TabContent>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Regular popup for messages */}
      {showPopup && !chestOpening && (
        <div
          className="popup-message"
          style={{
            position: "fixed",
            top: "50px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "15px",
            backgroundColor: popupColor,
            color: popupColor === "gray" ? "black" : "white",
            zIndex: 9999,
            border: "2px solid white",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            textAlign: "center",
          }}
        >
          {popupMessage}
        </div>
      )}
      
      {/* Chest Opening Modal */}
      {chestOpening && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(5px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(25, 25, 35, 0.95)",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "500px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
              overflow: "hidden",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              animation: "fadeIn 0.3s ease-out",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "rgba(0, 0, 0, 0.2)",
              }}
            >
              <h5 style={{ margin: 0, color: "#fff", fontWeight: "600" }}>Opening Chest</h5>
              <button
                onClick={() => setChestOpening(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  opacity: 0.7,
                  flexShrink: 0,
                  minWidth: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
              >
                ✕
              </button>
            </div>
            
            {/* Body */}
              <div style={{
                padding: '30px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}>
                {chestDisplayImage && (
                  <div style={{
                    width: '200px',
                    height: '200px',
                    margin: '0 auto 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(40, 44, 52, 0.6)',
                    borderRadius: '10px',
                    padding: '20px',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <img
                      src={chestDisplayImage}
                      alt="Chest Opening"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))'
                      }}
                    />
                  </div>
                )}
                <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                  marginTop: '10px'
                }}>
                  {popupMessage || 'Opening chest...'}
                </div>

                {chestRewardItems && chestRewardItems.length > 0 && (
                  <div
                    style={{
                      marginTop: 18,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
                      gap: 12,
                      width: '100%',
                      maxWidth: 420,
                    }}
                  >
                    {chestRewardItems.map((reward) => {
                      const item = storeItemsMap[reward.item_id];
                      const img = item?.image ? `/${item.image}` : '/items/pokeball.png';
                      const name = item?.name || reward.name || `Item ${reward.item_id}`;
                      return (
                        <div
                          key={reward.item_id}
                          style={{
                            background: 'rgba(40, 44, 52, 0.45)',
                            borderRadius: 10,
                            padding: 10,
                            border: '1px solid rgba(255, 255, 255, 0.12)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <img
                            src={img}
                            alt={name}
                            style={{ width: 36, height: 36, objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/items/pokeball.png';
                            }}
                          />
                          <div style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>
                            x{reward.amount}
                          </div>
                          <div style={{ color: '#b8c5d6', fontSize: 12, lineHeight: 1.1 }}>
                            {name}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div
                style={{
                  padding: '12px 20px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.2)',
                }}
              >
                <button
                  onClick={() => setChestOpening(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      
    </React.Fragment>
  );
};

export default ManageItems;
